import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaPhone, FaWhatsapp, FaFilePdf } from 'react-icons/fa';
import { LuMapPin, LuBuilding } from 'react-icons/lu';

// Imports para a nova lógica
import { dependenteAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ErroLoadData from '../components/ErroLoadData';
import { downloadFile } from '../utils/downloadUtils';

// --- Styled Components (sem alterações) ---

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    padding: 16px 12px 24px;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;

  h1 {
    font-size: 32px;
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 8px;
    background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    color: ${props => props.theme.colors.textSecondary};
    font-size: 18px;
    font-weight: 500;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
    h1 { font-size: 24px; }
    p { font-size: 15px; }
  }
`;

const Section = styled.section`
  background: ${props => props.theme.colors.white};
  padding: 32px;
  border-radius: 16px;
  margin-bottom: 24px;
  box-shadow: ${props => props.theme.shadows.card};
  border: 1px solid ${props => props.theme.colors.border};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 2px solid ${props => props.theme.colors.lightBlue};
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 24px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
  
  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const List = styled.ul`
  margin-left: 18px;
  color: ${props => props.theme.colors.grayDark};
  line-height: 1.7;
  li { 
    margin-bottom: 8px; 
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  ul { margin-top: 8px; }
  
  @media (max-width: 768px) {
    margin-left: 15px;
    font-size: 14px;
  }
  
  @media (max-width: 480px) {
    margin-left: 12px;
    font-size: 13px;
  }
`;

const Card = styled.div`
  background: ${props => props.theme.colors.backgroundAlt};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  
  @media (max-width: 768px) {
    padding: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const PhonesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  justify-content: center;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const PhoneItem = styled.a`
  background: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.borderLight};
  border-radius: 10px;
  padding: 14px 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  text-align: center;
  text-decoration: none;
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  ${props => props.isWhatsapp && `
    color: #25D366;
    border-color: #25D366;
    &:hover {
      background-color: #25D366;
      color: white;
    }
  `}

  &:hover {
    cursor: pointer;
    text-decoration: none;
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.card};
  }
  
  @media (max-width: 768px) {
    padding: 12px 14px;
    font-size: 14px;
  }
`;

const UnidadesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const UnidadeCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
`;

const UnidadeNome = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
`;

const UnidadeEndereco = styled.div`
  font-size: 14px;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  
  svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: ${props => props.theme.colors.secondary};
  }
`;

const Small = styled.small`
  display: block;
  margin-top: 8px;
  text-align: center;
  color: ${props => props.theme.colors.grayMedium};
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const PdfLinksContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 24px;
  justify-content: center;
`;

const PdfLinkButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  border: 1px solid transparent;
  border-radius: 10px;
  padding: 14px 20px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;

  &:hover {
    color: ${props => props.theme.colors.white};
    background: ${props => props.theme.colors.secondary};
    text-decoration: none;
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.card};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 14px;
  }
`;


const Coverage = () => {
  // --- NOVOS ESTADOS ---
  const [hasPetPlan, setHasPetPlan] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- NOVA LÓGICA DE BUSCA DE DADOS ---
  useEffect(() => {
    const checkPetPlan = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await dependenteAPI.getAll();

        if (response.data.success) {
          const { dependentes } = response.data.data;
          // Verifica se algum dependente tem um plano que começa com 'PET' (insensível a maiúsculas/minúsculas)
          const petPlanExists = dependentes.some(
            dep => dep.plano && dep.plano.toUpperCase().startsWith('PET')
          );
          setHasPetPlan(petPlanExists);
        } else {
          // Define um erro se a API retornar success: false
          setError(response.data.message || 'Erro ao verificar planos');
        }
      } catch (err) {
        console.error('Erro ao buscar dependentes para verificar plano PET:', err);
        setError('Não foi possível carregar os dados do plano. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    checkPetPlan();
  }, []); // O array vazio [] garante que isso rode apenas uma vez

  const [pagePhones, setPagePhones] = React.useState([]);
  const [pageDocs, setPageDocs] = React.useState(null);
  const [maintenanceItems, setMaintenanceItems] = React.useState([]);
  const [direitosAssociado, setDireitosAssociado] = React.useState([]);
  const [preparacaoCorpo, setPreparacaoCorpo] = React.useState([]);
  const [capelaSepultamento, setCapelaSepultamento] = React.useState({ incluso: [], nao_incluso: [] });
  const [condicoesCarencias, setCondicoesCarencias] = React.useState({ carencia: [], obito_durante_carencia: [] });
  const [formasPagamento, setFormasPagamento] = React.useState([]);
  const [infoInstitucional, setInfoInstitucional] = React.useState({ email: '', endereco: '', cnpj: '' });
  const [unidades, setUnidades] = React.useState({ sede: null, filiais: [] });

  React.useEffect(() => {
    let mounted = true;
    const loadPageData = async () => {
      try {
        const { paginaAPI } = await import('../services/api');
        const resp = await paginaAPI.getCobertura();
        const payload = resp?.data?.data || {};
        if (mounted) {
          setPagePhones(Array.isArray(payload.telefones) ? payload.telefones : []);
          setPageDocs(payload.documentos || null);
          setMaintenanceItems(Array.isArray(payload.manutencao_plano) ? payload.manutencao_plano : []);
          setDireitosAssociado(Array.isArray(payload.direitos_associado) ? payload.direitos_associado : []);
          setPreparacaoCorpo(Array.isArray(payload.preparacao_corpo) ? payload.preparacao_corpo : []);
          setCapelaSepultamento(payload.capela_sepultamento || { incluso: [], nao_incluso: [] });
          setCondicoesCarencias(payload.condicoes_carencias || { carencia: [], obito_durante_carencia: [] });
          setFormasPagamento(Array.isArray(payload.formas_pagamento) ? payload.formas_pagamento : []);
          setInfoInstitucional(payload.informacoes_institucionais || { email: '', endereco: '', cnpj: '' });
          setUnidades(payload.unidades || { sede: null, filiais: [] });
        }
      } catch (e) {
        // mantém silencioso; UI continua com fallback estático se necessário
      }
    };
    loadPageData();
    return () => { mounted = false; };
  }, []);

  const formatWhatsappLink = (number) => {
    const cleanedNumber = number.replace(/\D/g, '');
    return `https://wa.me/55${cleanedNumber}`;
  };

  // Helper para construir URL completa do documento
  const getDocumentUrl = (relativePath) => {
    if (!relativePath) return null;
    // Se já começa com http, retorna como está
    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
      return relativePath;
    }
    // Se começa com /api, usa o baseURL da API
    if (relativePath.startsWith('/api/')) {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      return `${apiBaseUrl}${relativePath}`;
    }
    // Caso contrário, retorna como está
    return relativePath;
  };

  // Helper para fazer download do documento
  const handleDownload = async (url, filename) => {
    try {
      const fullUrl = getDocumentUrl(url);
      if (!fullUrl) {
        console.error('URL do documento não disponível');
        return;
      }

      // Usar função utilitária compatível com iOS
      await downloadFile(fullUrl, filename);
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      alert('Erro ao baixar o arquivo. Tente novamente.');
    }
  };

  // Helper para renderizar texto com partes em negrito
  const renderTextWithBold = (item) => {
    if (!item) {
      return '';
    }
    if (typeof item === 'string') {
      return item;
    }
    if (typeof item === 'object' && item !== null) {
      if (item.texto !== undefined) {
        return (
          <>
            {item.texto || ''}
            {item.negrito && Array.isArray(item.negrito) && item.negrito.length > 0 && item.negrito.map((boldText, idx) => (
              <strong key={`bold-${idx}`}>{boldText}</strong>
            ))}
            {item.complemento || ''}
          </>
        );
      }
      // Se for um objeto mas não tem texto, tenta converter para string
      return String(item);
    }
    // Fallback para outros tipos
    return String(item);
  };

  // --- RENDERIZAÇÃO CONDICIONAL PARA LOADING E ERRO ---
  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErroLoadData />
      </PageContainer>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <PageContainer>
        <PageHeader>
          <h1>Cobertura do Plano</h1>
          <p>Informações oficiais do plano assistencial funerário</p>
        </PageHeader>

        <Section aria-labelledby="atendimento-24h">
          <SectionTitle id="atendimento-24h">Atendimento Funeral 24h</SectionTitle>
          <Card>
            <strong>Telefones</strong>
            <PhonesGrid style={{ marginTop: 12 }}>
              {(pagePhones && pagePhones.length > 0 ? pagePhones : []).map((p, idx) => {
                const isZap = String(p.tipo).toLowerCase() === 'whatsapp';
                const href = isZap ? formatWhatsappLink(p.numero) : `tel:+55${p.numero.replace(/\D/g, '')}`;
                return (
                  <PhoneItem key={`fone-${idx}`} href={href} target={isZap ? '_blank' : undefined} rel={isZap ? 'noopener noreferrer' : undefined} isWhatsapp={isZap}>
                    {isZap ? <FaWhatsapp /> : <FaPhone />} {p.rotulo}
                  </PhoneItem>
                );
              })}
            </PhonesGrid>
            <Small>Atendimento funeral, 24 horas por dia.</Small>
          </Card>
        </Section>

        {/* O resto do seu conteúdo JSX permanece o mesmo */}

        <Section aria-labelledby="direitos-associado">
          <SectionTitle id="direitos-associado">Direitos do Associado</SectionTitle>
          <List>
            {direitosAssociado.length > 0 ? direitosAssociado.map((item, idx) => {
              if (typeof item === 'object' && item.texto) {
                return (
                  <li key={`dir-${idx}`}>
                    {item.texto}
                    {item.subitens && item.subitens.length > 0 && (
                      <List>
                        {item.subitens.map((subitem, subidx) => (
                          <li key={`dir-sub-${idx}-${subidx}`}>{subitem}</li>
                        ))}
                      </List>
                    )}
                  </li>
                );
              }
              return <li key={`dir-${idx}`}>{item}</li>;
            }) : (
              <>
                <li>Ornamentação com flores (artificiais ou naturais da época)</li>
                <li>Remoção/traslado com cobertura de até 500 km (ida e volta)</li>
                <li>Urna (caixão) conforme o modelo escolhido no ato do contrato</li>
                <li>Véu mortuário</li>
                <li>Vestimenta do corpo (ato de vestir)</li>
                <li>Registro de óbito em cartório, se permitido por terceiros via despachante</li>
                <li>Coroa de flores</li>
                <li>
                  Jazigo Perpétuo Familiar: cobertura de abertura e fechamento (ou valor equivalente), em cemitérios municipais
                  <List>
                    <li>Benfeitorias e exumações são de responsabilidade do contratante/associado</li>
                  </List>
                </li>
              </>
            )}
          </List>
        </Section>
        <Section aria-labelledby="preparacao-corpo">
          <SectionTitle id="preparacao-corpo">Preparação do Corpo</SectionTitle>
          <List>
            {preparacaoCorpo.length > 0 ? preparacaoCorpo.map((item, idx) => {
              if (typeof item === 'object' && item.texto) {
                return (
                  <li key={`prep-${idx}`}>
                    {item.destaque && <strong>{item.texto}</strong>}
                    {!item.destaque && item.texto}
                    {item.complemento}
                  </li>
                );
              }
              return <li key={`prep-${idx}`}>{item}</li>;
            }) : (
              <>
                <li><strong>Tanatopraxia</strong> para óbitos ocorridos após mais de 24 horas</li>
                <li><strong>Embalsamamento</strong> exclusivo para traslados interestaduais</li>
                <li>Responsabilidade da contratada excluída em caso de demora dos familiares</li>
              </>
            )}
          </List>
        </Section>
        <Section aria-labelledby="capela-sepultamento">
          <SectionTitle id="capela-sepultamento">Capela e Sepultamento</SectionTitle>
          <TwoColumns>
            <div>
              <List>
                {capelaSepultamento.incluso && capelaSepultamento.incluso.length > 0 ? (
                  capelaSepultamento.incluso.map((item, idx) => (
                    <li key={`cap-incl-${idx}`}>{item}</li>
                  ))
                ) : (
                  <li>Sepultura em gaveta ou cova rasa em cemitérios municipais do Rio de Janeiro, conforme disponibilidade</li>
                )}
              </List>
            </div>
            <Card>
              <strong>Não incluso</strong>
              <List>
                {capelaSepultamento.nao_incluso && capelaSepultamento.nao_incluso.length > 0 ? (
                  capelaSepultamento.nao_incluso.map((item, idx) => (
                    <li key={`cap-nao-${idx}`}>{item}</li>
                  ))
                ) : (
                  <>
                    <li>Isenção de taxas de manutenção de sepultura dos 2 últimos anos.</li>
                    <li>Aluguel de gaveta especial</li>
                    <li>Revestimentos ou melhorias</li>
                  </>
                )}
              </List>
            </Card>
          </TwoColumns>
        </Section>
        <Section aria-labelledby="condicoes-carencias">
          <SectionTitle id="condicoes-carencias">Condições e Carências</SectionTitle>
          <TwoColumns>
            <Card>
              <strong>Carência</strong>
              <List>
                {condicoesCarencias.carencia && condicoesCarencias.carencia.length > 0 ? (
                  condicoesCarencias.carencia.map((item, idx) => (
                    <li key={`caren-${idx}`}>{renderTextWithBold(item)}</li>
                  ))
                ) : (
                  <>
                    <li>Maiores de 60 anos: <strong>120 dias (4 meses)</strong></li>
                    <li>Até 59 anos: <strong>60 dias (2 meses)</strong></li>
                  </>
                )}
              </List>
            </Card>
            <Card>
              <strong>Óbito durante a carência</strong>
              <List>
                {condicoesCarencias.obito_durante_carencia && condicoesCarencias.obito_durante_carencia.length > 0 ? (
                  condicoesCarencias.obito_durante_carencia.map((item, idx) => (
                    <li key={`obito-${idx}`}>{renderTextWithBold(item)}</li>
                  ))
                ) : (
                  <>
                    <li>Se o associado residir e for sepultado no município do Rio de Janeiro: <strong>desconto</strong> no valor da tabela vigente</li>
                    <li>Funeral pode ser parcelado em até <strong>10x sem juros</strong> no cartão de crédito</li>
                  </>
                )}
              </List>
            </Card>
          </TwoColumns>
        </Section>
        <Section aria-labelledby="manutencao-plano">
          <SectionTitle id="manutencao-plano">Manutenção do Plano</SectionTitle>
          <List>
            {(maintenanceItems && maintenanceItems.length > 0 ? maintenanceItems : []).map((item, i) => (
              <li key={`mnt-${i}`}>{renderTextWithBold(item)}</li>
            ))}
          </List>

          <PdfLinksContainer>
            <PdfLinkButton
              onClick={(e) => {
                e.preventDefault();
                const url = (pageDocs && pageDocs.contrato) || '/api/documentos/contrato.pdf';
                handleDownload(url, 'contrato-plano-assistencial.pdf');
              }}
            >
              <FaFilePdf /> Baixar Contrato
            </PdfLinkButton>
            <PdfLinkButton
              onClick={(e) => {
                e.preventDefault();
                const url = (pageDocs && pageDocs.direitos) || '/api/documentos/direito-associados.pdf';
                handleDownload(url, 'direitos-do-associado.pdf');
              }}
            >
              <FaFilePdf /> Direitos do Associado
            </PdfLinkButton>

            {/* --- NOVOS BOTÕES RENDERIZADOS CONDICIONALMENTE --- */}
            {hasPetPlan && (
              <>
                <PdfLinkButton
                  onClick={(e) => {
                    e.preventDefault();
                    const url = (pageDocs && pageDocs.contrato_pet) || '/api/documentos/contrato-pet.pdf';
                    handleDownload(url, 'contrato-plano-pet.pdf');
                  }}
                >
                  <FaFilePdf /> Baixar Contrato PET
                </PdfLinkButton>
                <PdfLinkButton
                  onClick={(e) => {
                    e.preventDefault();
                    const url = (pageDocs && pageDocs.direitos_pet) || '/api/documentos/direito-associados-pet.pdf';
                    handleDownload(url, 'direitos-do-associado-pet.pdf');
                  }}
                >
                  <FaFilePdf /> Direito Pet
                </PdfLinkButton>
              </>
            )}
          </PdfLinksContainer>
        </Section>
        <Section aria-labelledby="pagamento-institucional">
          <SectionTitle id="pagamento-institucional">Pagamento e Informações Institucionais</SectionTitle>
          <TwoColumns>
            <Card>
              <strong>Formas de pagamento</strong>
              <List>
                {formasPagamento.length > 0 ? (
                  formasPagamento.map((item, idx) => (
                    <li key={`pag-${idx}`}>{item}</li>
                  ))
                ) : (
                  <li>Cartões de crédito e débito</li>
                )}
              </List>
            </Card>
            <Card>
              <strong>Contato e dados</strong>
              <List>
                <li><strong>E-mail</strong>: {infoInstitucional.email || 'contato@santacasacopacabana.com.br'}</li>
                <li><strong>Endereço</strong>: {infoInstitucional.endereco || 'Rua Arquias Cordeiro, 257'}</li>
                <li><strong>CNPJ</strong>: {infoInstitucional.cnpj || '04.074.518/0001-20'}</li>
              </List>
            </Card>
          </TwoColumns>
        </Section>

        <Section aria-labelledby="nossas-unidades">
          <SectionTitle id="nossas-unidades">Onde nos encontrar</SectionTitle>

          {unidades.sede && (
            <div style={{ marginBottom: '32px' }}>
              <strong style={{ display: 'block', marginBottom: '12px', fontSize: '14px', textTransform: 'uppercase', color: '#64748b' }}>Sede Administrativa</strong>
              <UnidadeCard style={{ background: '#fff', borderColor: '#cbd5e1' }}>
                <UnidadeNome>
                  <LuBuilding size={18} /> {unidades.sede.nome}
                </UnidadeNome>
                <UnidadeEndereco>
                  <LuMapPin size={16} /> {unidades.sede.endereco}
                </UnidadeEndereco>
              </UnidadeCard>
            </div>
          )}

          {unidades.filiais && unidades.filiais.length > 0 && (
            <>
              <strong style={{ display: 'block', marginBottom: '12px', fontSize: '14px', textTransform: 'uppercase', color: '#64748b' }}>Nossas Filiais</strong>
              <UnidadesGrid>
                {unidades.filiais.map((filial, idx) => (
                  <UnidadeCard key={`filial-${idx}`}>
                    <UnidadeNome>
                      <LuBuilding size={16} /> {filial.nome}
                    </UnidadeNome>
                    <UnidadeEndereco>
                      <LuMapPin size={14} /> {filial.endereco}
                    </UnidadeEndereco>
                  </UnidadeCard>
                ))}
              </UnidadesGrid>
            </>
          )}
        </Section>
      </PageContainer>
    </motion.div>
  );
};

export default Coverage;